import React, { useEffect, useRef, useState } from "react";
import { downloadLogo } from "./logo";

export function SettingsSelect({
  onExport,
  onImport,
  sizeVar = "var(--toolbar-button-size)",
}: {
  onExport: () => void;
  onImport: (text: string) => void;
  /** CSS size (can be a var). Defaults to var(--toolbar-button-size). */
  sizeVar?: string;
}) {
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const importRef = useRef<HTMLTextAreaElement | null>(null);

  // Close menus on outside click / Esc
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !menuRef.current?.contains(t)) {
        setOpen(false);
        setImportOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (importOpen) {
          setImportOpen(false);
          setTimeout(() => menuRef.current?.focus(), 0);
        } else {
          setOpen(false);
          btnRef.current?.focus();
        }
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [importOpen]);

  // Focus textarea when import popover opens
  useEffect(() => {
    if (importOpen) {
      setTimeout(() => importRef.current?.focus(), 0);
    }
  }, [importOpen]);

  const handleExport = () => {
    onExport();
    setOpen(false);
  };

  const handleImportOpen = () => {
    setImportText("");
    setImportOpen(true);
  };

  const handleImportConfirm = () => {
    const text = importText.trim();
    if (text) onImport(text);
    setImportOpen(false);
    setOpen(false);
    btnRef.current?.focus();
  };

  return (
    <div
      style={{
        position: "relative",
        display: "inline-block",
        width: sizeVar,
        height: sizeVar,
        marginTop: 8,
      }}
    >
      <button
        ref={btnRef}
        type="button"
        title="Debug"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setTimeout(() => menuRef.current?.focus(), 0);
          }
        }}
        style={{
          width: "100%",
          height: "100%",
          background: "transparent",
          color: "#fff",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          appearance: "none",
          WebkitAppearance: "none",
        }}
      >
        {/* Gear icon */}
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.09a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.09a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          tabIndex={-1}
          aria-label="Debug menu"
          onKeyDown={(e) => {
            // simple keyboard: Enter activates focused button via click
            if (e.key === "Escape") {
              e.preventDefault();
              setOpen(false);
              btnRef.current?.focus();
            }
          }}
          style={{
            position: "absolute",
            top: `calc(${sizeVar} + 6px)`,
            right: 0,
            minWidth: 160,
            background: "rgba(20,20,20,0.98)",
            color: "#fff",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,255,255,0.08)",
            padding: 6,
            zIndex: 9999,
            outline: "none",
            userSelect: "none",
          }}
        >
          {/* Export */}
          <div
            role="menuitem"
            onClick={handleExport}
            style={menuItemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = hoverBg)}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            Export
          </div>

          {/* Import */}
          <div
            role="menuitem"
            onClick={handleImportOpen}
            style={menuItemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = hoverBg)}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            Import
          </div>
          {/* Download Logo */}
          <div
            role="menuitem"
            onClick={() => {
              downloadLogo();
              setOpen(false);
            }}
            style={menuItemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = hoverBg)}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            Download Logo
          </div>

          {importOpen && (
            <div
              role="dialog"
              aria-label="Import data"
              aria-modal="false"
              style={{
                marginTop: 6,
                padding: 8,
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(12,12,12,0.98)",
              }}
              onKeyDown={(e) => {
                if (
                  (e.metaKey || e.ctrlKey) &&
                  e.key.toLowerCase() === "enter"
                ) {
                  e.preventDefault();
                  handleImportConfirm();
                }
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
                Paste data to import:
              </div>
              <textarea
                ref={importRef}
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                rows={4}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  resize: "vertical",
                  background: "rgba(255,255,255,0.06)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 6,
                  padding: "8px 10px",
                  outline: "none",
                }}
              />
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginTop: 8,
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setImportOpen(false);
                    setTimeout(() => menuRef.current?.focus(), 0);
                  }}
                  style={secondaryBtnStyle}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleImportConfirm}
                  style={primaryBtnStyle}
                  title="Import (⌘/Ctrl + Enter)"
                >
                  Import
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const hoverBg = "rgba(255,255,255,0.08)";

const menuItemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 10px",
  borderRadius: 6,
  cursor: "pointer",
  background: "transparent",
};

const primaryBtnStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.2)",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: 6,
  padding: "6px 10px",
  cursor: "pointer",
};

const secondaryBtnStyle: React.CSSProperties = {
  background: "transparent",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: 6,
  padding: "6px 10px",
  cursor: "pointer",
};
